import type {
    ConnectionDiag,
    SqliteCloseOptions,
    ConnectionOptions,
    DatabaseDiagnostics,
    ExecResult,
    PreparedStatement,
    QueryOptions,
    SqlParam,
    SqliteConnection,
    SqliteDatabase,
    SqliteFlushLocalOptions,
    SqliteStatementResult,
    SqliteTransaction,
    SyncEvent,
    SyncMode,
    SyncResult,
    TransactionOptions,
} from "./sqliteDatabase"
import { SqliteError } from "./sqliteDatabase"
import {
    buildBrowserSqliteHeadBlobId,
    buildBrowserSqliteWorkspaceName,
    type BrowserSqliteClientWorkerRequest,
    type BrowserSqliteConnectResult,
    type BrowserSqliteControlWorkerEvent,
    type BrowserSqliteControlWorkerRequest,
    type BrowserSqliteInitResult,
    type BrowserSqliteOpenConfig,
    type BrowserSqliteWorkerResponse,
    type SerializedSqliteError,
} from "./browserSqliteProtocol"
import { dispatchBrowserRuntimeTraceEvent } from "./browserRuntimeTrace"
import type { WorkspaceSyncPolicy } from "./types"
import { sdkConsole } from "./logger"
import { buildSqlite3MtDurableIdbName, SQLITE3MT_DURABLE_IDB_NAME } from "./sqlite3mt/durable-idb"
import { createManagedWalCheckpointResult } from "./sqliteWalCheckpointPragma"
import { shouldReopenConnectionAfterSqliteFailure } from "./sqliteTransactionRecovery"

interface PendingRequest {
    resolve(value: unknown): void
    reject(reason: unknown): void
    timeout: ReturnType<typeof setTimeout> | null
}

function logSqlStatement(args: {
    connId: number
    sql: string
    paramsCount: number
    durationMs: number
    rowCount?: number
    changes?: number
    ok: boolean
    error?: unknown
    txId?: number
}): void {
    sdkConsole.debug('[sqlite3mt:sql] statement', {
        connId: args.connId,
        ...(args.txId != null ? { txId: args.txId } : {}),
        sql: args.sql,
        paramsCount: args.paramsCount,
        durationMs: Math.round(args.durationMs),
        ...(args.rowCount != null ? { rowCount: args.rowCount } : {}),
        ...(args.changes != null ? { changes: args.changes } : {}),
        ok: args.ok,
        ...(args.error != null
            ? { error: args.error instanceof Error ? args.error.message : String(args.error) }
            : {}),
    })
}

type RpcPayload<TRequest> =
    TRequest extends { kind: 'request'; requestId: number }
        ? Omit<TRequest, 'kind' | 'requestId'>
        : never

const CONNECTION_CLOSE_GRACE_MS = 500
const DEFAULT_BROWSER_SQLITE_RPC_TIMEOUT_MS = 300_000
const MAX_BROWSER_SQLITE_RPC_TIMEOUT_MS = 900_000
const BROWSER_SQLITE_DURABLE_CLIENT_ID_PREFIX = 'secureblob.sqlite3mt.durable-client.v1:'
const BROWSER_SQLITE_DURABLE_LOCK_PREFIX = 'secureblob-sqlite3mt-durable:'

interface BrowserDurableScopeLease {
    readonly scope: string
    readonly isSecondaryScope: boolean
    release(): Promise<void>
}

export interface BrowserSqliteDurableCacheInfo {
    readonly headBlobId: string
    readonly path: string
    readonly storageKey: string
    readonly clientId: string | null
    readonly baseScope: string | null
    readonly durableDbNamePrefix: string | null
    readonly durableDbNames: readonly string[] | null
    readonly activeSlots: readonly number[]
}

export interface BrowserSqliteDurableCacheResetResult extends BrowserSqliteDurableCacheInfo {
    readonly rotatedClientId: boolean
    readonly deletedDbNames: readonly string[]
    readonly skippedDbNames: readonly string[]
    readonly deleteErrors: readonly { readonly dbName: string; readonly error: string }[]
    /**
     * Operator-facing diagnostic. Non-empty when the reset could not achieve the
     * intended effect, so callers do not mistake a no-op for success. Covers the
     * two real failure modes: (a) the blobId looked like an already-built
     * headBlobId (double-encoding yields no matching clientId), and (b) no
     * client id existed for this lane yet, so there was nothing to rotate.
     */
    readonly hint: readonly string[]
}

type BrowserLockManager = {
    request<T>(
        name: string,
        options: { mode: 'exclusive'; ifAvailable: true },
        callback: (lock: unknown | null) => T | Promise<T>,
    ): Promise<T>
}

const browserDurableFallbackClientIds = new Map<string, string>()
const activeBrowserDurableSlots = new Map<string, Set<number>>()

function readBrowserSqliteRpcTimeoutMs(): number {
    const processLike = globalThis as typeof globalThis & {
        __secureblobComponentJobRuntimeEnv?: Record<string, string | undefined>
        process?: { env?: Record<string, string | undefined> }
    }
    const value = processLike.__secureblobComponentJobRuntimeEnv?.SECUREBLOB_BROWSER_SQLITE_RPC_TIMEOUT_MS
        ?? processLike.process?.env?.SECUREBLOB_BROWSER_SQLITE_RPC_TIMEOUT_MS
    if (value == null || value.trim().length === 0) {
        return DEFAULT_BROWSER_SQLITE_RPC_TIMEOUT_MS
    }
    const parsed = Number(value)
    if (Number.isFinite(parsed) && parsed >= 1 && parsed <= MAX_BROWSER_SQLITE_RPC_TIMEOUT_MS) {
        return Math.trunc(parsed)
    }
    sdkConsole.warn('[sqlite3mt:browser-main] ignoring invalid browser RPC timeout override', {
        value,
        fallback: DEFAULT_BROWSER_SQLITE_RPC_TIMEOUT_MS,
    })
    return DEFAULT_BROWSER_SQLITE_RPC_TIMEOUT_MS
}

function browserSqliteRpcUsesWatchdog(request: object): boolean {
    if (!('op' in request)) {
        return true
    }

    const op = String(request.op)
    if (
        op === 'db-connect'
        || op === 'db-start-sync'
        || op === 'db-set-workspace-sync-policy'
        || op === 'db-sync-now'
        || op === 'db-flush'
        || op === 'db-close'
        || op === 'conn-execute'
        || op === 'conn-close'
        || op === 'tx-begin'
        || op === 'tx-execute'
        || op === 'tx-commit'
        || op === 'tx-rollback'
    ) {
        return false
    }
    return true
}

function browserSqliteRpcTimeoutIsTerminal(request: object): boolean {
    if (!('op' in request)) {
        return true
    }

    const op = String(request.op)
    return op !== 'db-release-client'
}

function randomBrowserSqliteId(): string {
    const cryptoLike = globalThis.crypto as Crypto | undefined
    if (typeof cryptoLike?.randomUUID === 'function') {
        return cryptoLike.randomUUID()
    }

    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

function getBrowserSqliteSessionStorage(): Storage | null {
    try {
        return typeof globalThis.sessionStorage === 'undefined' ? null : globalThis.sessionStorage
    } catch {
        return null
    }
}

function resolveBrowserDurableClientId(headBlobId: string): string {
    const key = `${BROWSER_SQLITE_DURABLE_CLIENT_ID_PREFIX}${headBlobId}`
    const sessionStorage = getBrowserSqliteSessionStorage()
    if (sessionStorage != null) {
        const existing = sessionStorage.getItem(key)
        if (existing && existing.length > 0) {
            return existing
        }

        const created = randomBrowserSqliteId()
        sessionStorage.setItem(key, created)
        return created
    }

    const existing = browserDurableFallbackClientIds.get(headBlobId)
    if (existing) {
        return existing
    }

    const created = randomBrowserSqliteId()
    browserDurableFallbackClientIds.set(headBlobId, created)
    return created
}

function buildBrowserDurableScope(baseScope: string, slot: number): string {
    return slot === 1 ? baseScope : `${baseScope}:live-slot-${slot}`
}

function browserDurableClientStorageKey(headBlobId: string): string {
    return `${BROWSER_SQLITE_DURABLE_CLIENT_ID_PREFIX}${headBlobId}`
}

function buildBrowserDurableBaseScope(headBlobId: string, clientId: string): string {
    return `${headBlobId}:browser-client:${clientId}`
}

function durableDbNameMatchesBaseScope(dbName: string, baseScope: string): boolean {
    const exactName = buildSqlite3MtDurableIdbName(baseScope)
    return dbName === exactName || dbName.startsWith(`${exactName}%3Alive-slot-`)
}

function getIndexedDbLike(): IDBFactory | null {
    try {
        return typeof globalThis.indexedDB === 'undefined' ? null : globalThis.indexedDB
    } catch {
        return null
    }
}

async function listDurableDbNamesForBaseScope(baseScope: string): Promise<string[] | null> {
    const indexedDb = getIndexedDbLike() as (IDBFactory & {
        databases?: () => Promise<Array<{ name?: string | null }>>
    }) | null
    if (typeof indexedDb?.databases !== 'function') return null
    const databases = await indexedDb.databases()
    return databases
        .map(db => db.name ?? '')
        .filter(name => name.length > 0 && durableDbNameMatchesBaseScope(name, baseScope))
        .sort()
}

/**
 * Brist C fix: list durable DBs that belong to the same headBlobId but to a
 * DIFFERENT clientId (i.e. leftover databases from earlier clientId rotations).
 * Such DBs cannot be reached anymore once the clientId rotates, so they leak
 * until explicitly removed. Only durable sqlite3mt DBs are considered.
 *
 * The headBlobId is the part before `:browser-client:` in the encoded scope.
 */
async function listLeakedDurableDbNamesForHeadBlobId(headBlobId: string, keepBaseScope: string | null): Promise<string[]> {
    const indexedDb = getIndexedDbLike() as (IDBFactory & {
        databases?: () => Promise<Array<{ name?: string | null }>>
    }) | null
    if (typeof indexedDb?.databases !== 'function') return []
    const leaked: string[] = []
    const durablePrefix = `${SQLITE3MT_DURABLE_IDB_NAME}:`
    const headBlobIdEncoded = encodeURIComponent(headBlobId)
    const keepScopePrefix = keepBaseScope != null ? encodeURIComponent(keepBaseScope) : null
    const databases = await indexedDb.databases()
    for (const db of databases) {
        const name = db.name ?? ''
        if (!name.startsWith(durablePrefix)) continue
        // The encoded scope is everything after the durable prefix colon.
        const encodedScope = name.slice(durablePrefix.length)
        // Match this headBlobId (same workspace/lane) but not the kept clientId.
        if (!encodedScope.startsWith(headBlobIdEncoded)) continue
        if (keepScopePrefix != null && encodedScope.startsWith(keepScopePrefix)) continue
        leaked.push(name)
    }
    return leaked.sort()
}

function fallbackDurableDbNamesForBaseScope(baseScope: string): string[] {
    const names = [buildSqlite3MtDurableIdbName(baseScope)]
    for (let slot = 2; slot <= 32; slot += 1) {
        names.push(buildSqlite3MtDurableIdbName(buildBrowserDurableScope(baseScope, slot)))
    }
    return names
}

function deleteIndexedDb(name: string, timeoutMs: number): Promise<'deleted' | 'skipped'> {
    const indexedDb = getIndexedDbLike()
    if (indexedDb == null) return Promise.resolve('skipped')
    return new Promise((resolve, reject) => {
        let settled = false
        let timeout: ReturnType<typeof setTimeout> | undefined
        const finish = (result: 'deleted' | 'skipped', error?: unknown) => {
            if (settled) return
            settled = true
            if (timeout !== undefined) clearTimeout(timeout)
            if (error != null) reject(error)
            else resolve(result)
        }
        try {
            const request = indexedDb.deleteDatabase(name)
            request.onsuccess = () => finish('deleted')
            request.onerror = () => finish('skipped', request.error ?? new Error(`deleteDatabase failed for ${name}`))
            request.onblocked = () => finish('skipped')
            timeout = setTimeout(() => finish('skipped'), Math.max(1, timeoutMs))
        } catch (error) {
            finish('skipped', error)
        }
    })
}

/**
 * Heuristic detector for the most common reset misuse: passing an already-built
 * headBlobId (from storageDiagnostics().headBlobId) as the raw `blobId`.
 * buildBrowserSqliteHeadBlobId produces `<blobId>:sqlite3mt:<base64(path)>`, so
 * a real workspace blobId never contains the `:sqlite3mt:` carrier separator.
 * Passing a headBlobId double-encodes the path and yields a clientId of null.
 */
function blobIdLooksLikeHeadBlobId(blobId: string): boolean {
    return blobId.includes(':sqlite3mt:')
}

export async function inspectBrowserSqliteDurableCache(args: Pick<BrowserSqliteOpenConfig, 'blobId' | 'path'>): Promise<BrowserSqliteDurableCacheInfo> {
    const headBlobId = buildBrowserSqliteHeadBlobId(args.blobId, args.path)
    const storageKey = browserDurableClientStorageKey(headBlobId)
    const sessionStorageRef = getBrowserSqliteSessionStorage()
    const clientId = sessionStorageRef?.getItem(storageKey) || browserDurableFallbackClientIds.get(headBlobId) || null
    const baseScope = clientId != null ? buildBrowserDurableBaseScope(headBlobId, clientId) : null
    const activeSlots = baseScope != null ? [...(activeBrowserDurableSlots.get(baseScope) ?? [])].sort((a, b) => a - b) : []
    const durableDbNames = baseScope != null ? await listDurableDbNamesForBaseScope(baseScope) : null
    return {
        headBlobId,
        path: args.path,
        storageKey,
        clientId,
        baseScope,
        durableDbNamePrefix: baseScope != null ? buildSqlite3MtDurableIdbName(baseScope) : null,
        durableDbNames,
        activeSlots,
    }
}

export async function resetBrowserSqliteDurableCache(
    args: Pick<BrowserSqliteOpenConfig, 'blobId' | 'path'>,
    options?: { readonly deleteIndexedDbTimeoutMs?: number },
): Promise<BrowserSqliteDurableCacheResetResult> {
    const info = await inspectBrowserSqliteDurableCache(args)
    const deletedDbNames: string[] = []
    const skippedDbNames: string[] = []
    const deleteErrors: Array<{ dbName: string; error: string }> = []

    const sessionStorageRef = getBrowserSqliteSessionStorage()
    if (sessionStorageRef != null) {
        sessionStorageRef.removeItem(info.storageKey)
    }
    browserDurableFallbackClientIds.delete(info.headBlobId)

    const dbNames = info.baseScope == null
        ? []
        : info.durableDbNames ?? fallbackDurableDbNamesForBaseScope(info.baseScope)
    for (const dbName of dbNames) {
        try {
            const result = await deleteIndexedDb(dbName, options?.deleteIndexedDbTimeoutMs ?? 500)
            if (result === 'deleted') deletedDbNames.push(dbName)
            else skippedDbNames.push(dbName)
        } catch (error) {
            deleteErrors.push({
                dbName,
                error: error instanceof Error ? error.message : String(error),
            })
        }
    }

    // Brist C fix: also sweep durable DBs left behind by earlier clientId
    // rotations for the same headBlobId. These cannot be reached after rotation
    // and would otherwise accumulate (each rotation leaves the previous
    // clientId's DB plus any live-slot DBs behind). Best-effort; DBs that are
    // still open by another tab will be reported as skipped.
    const leakedDbNames = await listLeakedDurableDbNamesForHeadBlobId(info.headBlobId, info.baseScope)
    for (const dbName of leakedDbNames) {
        if (deletedDbNames.includes(dbName) || skippedDbNames.includes(dbName)) continue
        try {
            const result = await deleteIndexedDb(dbName, options?.deleteIndexedDbTimeoutMs ?? 500)
            if (result === 'deleted') deletedDbNames.push(dbName)
            else skippedDbNames.push(dbName)
        } catch (error) {
            deleteErrors.push({
                dbName,
                error: error instanceof Error ? error.message : String(error),
            })
        }
    }

    const hint: string[] = []
    if (blobIdLooksLikeHeadBlobId(args.blobId)) {
        // The caller almost certainly copied storageDiagnostics().headBlobId into
        // the blobId field. That double-encodes the path, so clientId is null and
        // nothing actually rotates. Point at the raw workspace blob id.
        hint.push(
            'blobId looks like an already-built headBlobId (contains ":sqlite3mt:"). '
            + 'Pass the raw workspace blob id, not the headBlobId from storageDiagnostics.',
        )
    }
    if (info.clientId == null) {
        hint.push(
            'no durable client id found for this blobId+path; rotatedClientId is false and '
            + 'no IndexedDB stores were targeted. Either no client was ever opened for this '
            + 'lane, or the blobId/path do not match an active lane.',
        )
    } else if (skippedDbNames.length > 0) {
        hint.push(
            'clientId was rotated (next open re-bootstraps from the remote head) but '
            + `${skippedDbNames.length} IndexedDB store(s) could not be deleted because the `
            + 'tab still has them open; the rotation is what makes the reload effective.',
        )
    }

    return {
        ...info,
        rotatedClientId: info.clientId != null,
        deletedDbNames,
        skippedDbNames,
        deleteErrors,
        hint,
    }
}

/**
 * Proactively sweep durable IndexedDB stores left behind by earlier clientId
 * rotations for the same workspace/lane. This is the automatic counterpart to
 * the leak-sweep performed inside resetBrowserSqliteDurableCache: it targets
 * only DBs that no active connection can reach (a DIFFERENT clientId than the
 * current one), so it never disturbs the live cache.
 *
 * Safe to call at any time. Best-effort; DBs still open by another tab are
 * reported as skipped (the browser blocks deleteDatabase while a connection
 * holds the DB open). Never throws.
 *
 * Returns the list of deleted and skipped DB names so callers (e.g. a
 * periodic GC hook on sync-ok) can observe effectiveness.
 */
export async function sweepLeakedBrowserSqliteDurableDatabases(
    args: Pick<BrowserSqliteOpenConfig, 'blobId' | 'path'>,
    options?: { readonly deleteIndexedDbTimeoutMs?: number },
): Promise<{ readonly deletedDbNames: string[]; readonly skippedDbNames: string[] }> {
    const deletedDbNames: string[] = []
    const skippedDbNames: string[] = []
    try {
        const info = await inspectBrowserSqliteDurableCache(args)
        if (info.baseScope == null) {
            return { deletedDbNames, skippedDbNames }
        }
        const leakedDbNames = await listLeakedDurableDbNamesForHeadBlobId(info.headBlobId, info.baseScope)
        for (const dbName of leakedDbNames) {
            try {
                const result = await deleteIndexedDb(dbName, options?.deleteIndexedDbTimeoutMs ?? 500)
                if (result === 'deleted') {
                    deletedDbNames.push(dbName)
                } else {
                    skippedDbNames.push(dbName)
                }
            } catch {
                skippedDbNames.push(dbName)
            }
        }
    } catch {
        // Non-fatal: a failed sweep leaves the leaked DBs in place; the next
        // reset or sweep retry will catch them.
    }
    return { deletedDbNames, skippedDbNames }
}


function resolveBrowserLockManager(): BrowserLockManager | null {
    const nav = globalThis.navigator as (Navigator & { locks?: BrowserLockManager }) | undefined
    return nav?.locks ?? null
}

async function tryAcquireBrowserDurableLock(scope: string): Promise<(() => Promise<void>) | null> {
    const lockManager = resolveBrowserLockManager()
    if (lockManager == null) {
        return async () => {}
    }

    let releaseLock: (() => void) | null = null
    let requestDone: Promise<unknown> = Promise.resolve()
    let resolveStarted!: (release: (() => Promise<void>) | null) => void
    const started = new Promise<(() => Promise<void>) | null>(resolve => {
        resolveStarted = resolve
    })

    requestDone = lockManager.request(
        `${BROWSER_SQLITE_DURABLE_LOCK_PREFIX}${scope}`,
        { mode: 'exclusive', ifAvailable: true },
        async lock => {
            if (lock == null) {
                resolveStarted(null)
                return
            }

            await new Promise<void>(resolve => {
                releaseLock = resolve
                resolveStarted(async () => {
                    const release = releaseLock
                    releaseLock = null
                    release?.()
                    await requestDone.catch(() => undefined)
                })
            })
        },
    ).catch(error => {
        sdkConsole.warn('[sqlite3mt:browser-main] durable scope lock failed', {
            scope,
            error: error instanceof Error ? error.message : String(error),
        })
        resolveStarted(null)
    })

    return await started
}

async function acquireBrowserDurableScopeLease(config: BrowserSqliteOpenConfig): Promise<BrowserDurableScopeLease | null> {
    if (config.useSqlite3Mt?.persistence === 'memory') {
        return null
    }

    const headBlobId = buildBrowserSqliteHeadBlobId(config.blobId, config.path)
    const clientId = resolveBrowserDurableClientId(headBlobId)
    const baseScope = buildBrowserDurableBaseScope(headBlobId, clientId)

    let activeSlots = activeBrowserDurableSlots.get(baseScope)
    if (activeSlots == null) {
        activeSlots = new Set()
        activeBrowserDurableSlots.set(baseScope, activeSlots)
    }

    for (let slot = 1; ; slot += 1) {
        if (activeSlots.has(slot)) {
            continue
        }

        const scope = buildBrowserDurableScope(baseScope, slot)
        const releaseLock = await tryAcquireBrowserDurableLock(scope)
        if (releaseLock == null) {
            continue
        }

        activeSlots.add(slot)
        let released = false
        return {
            scope,
            isSecondaryScope: slot !== 1,
            release: async () => {
                if (released) {
                    return
                }
                released = true
                activeSlots.delete(slot)
                if (activeSlots.size === 0) {
                    activeBrowserDurableSlots.delete(baseScope)
                }
                await releaseLock()
                // Proactive GC (Brist C, cykel 122): after releasing this scope,
                // sweep durable DBs left behind by earlier clientId rotations for
                // the same headBlobId. These cannot be reached by the current
                // clientId and would otherwise accumulate until a manual reset.
                // Fire-and-forget; the helper swallows errors and onblocked.
                void listLeakedDurableDbNamesForHeadBlobId(headBlobId, baseScope).then(async leaked => {
                    for (const name of leaked) {
                        try {
                            await deleteIndexedDb(name, 500)
                        } catch {
                            // best-effort
                        }
                    }
                }).catch(() => {
                    // non-fatal
                })
                if (slot !== 1) {
                    const releasedSlotDbName = buildSqlite3MtDurableIdbName(scope)
                    void deleteIndexedDb(releasedSlotDbName, 500).catch(() => {
                        // best-effort: a blocked delete just leaves the released
                        // slot for a later reset/sweep, never a hard close error.
                    })
                }
            },
        }
    }
}

function sqliteCodeNameFromCode(code: number): string {
    switch (code) {
        case 1:
            return 'SQLITE_ERROR'
        case 5:
            return 'SQLITE_BUSY'
        case 6:
            return 'SQLITE_LOCKED'
        case 8:
            return 'SQLITE_READONLY'
        case 13:
            return 'SQLITE_FULL'
        case 19:
            return 'SQLITE_CONSTRAINT'
        default:
            return `SQLITE_${code}`
    }
}

function deserializeError(error: SerializedSqliteError): Error {
    const parsedCodeName = error.message.match(/\b(SQLITE_[A-Z0-9_]+)\b/)?.[1]
    if (typeof error.code === 'number' || parsedCodeName != null || error.name === 'SQLite3Error') {
        const code = typeof error.code === 'number' ? error.code : 1
        const sqliteError = new SqliteError(
            error.message,
            code,
            error.codeName ?? parsedCodeName ?? sqliteCodeNameFromCode(code),
        )
        if (error.stack) sqliteError.stack = error.stack
        return sqliteError
    }

    const err = new Error(error.message)
    err.name = error.name
    if (error.stack) err.stack = error.stack
    return err
}

function emptyDiagnostics(workspace: string): DatabaseDiagnostics {
    return {
        timestamp: 0,
        workspace,
        memory: {
            sab1Size: 0,
            sab2Size: 0,
            sab3Size: 0,
            totalSabBytes: 0,
            pageWindowSlots: 0,
            pageWindowUsed: 0,
            dirtyPageCount: 0,
            pageSize: 0,
            dbPageCount: 0,
            sabSupported: typeof SharedArrayBuffer !== 'undefined',
        },
        wal: {
            mxFrame: 0,
            walDataSize: 0,
            frameCount: 0,
            lastCheckpointFrame: 0,
            framesSinceCheckpoint: 0,
            autoCheckpointThreshold: 0,
        },
        connections: [],
        sync: {
            mode: 'concurrent' as SyncMode,
            state: 'idle',
            isSyncing: false,
            cycleCount: 0,
            lastResult: null,
            lastError: null,
            lastSyncAt: null,
            nextSyncAt: null,
            pendingPushPages: 0,
            remoteEpoch: 0,
            localEpoch: 0,
            casRetryCount: 0,
            lastConflictCount: 0,
            leaseId: null,
            leaseTtlRemainingMs: null,
        },
        locks: {
            writeLock: 0,
            checkpointLock: 0,
            recoverLock: 0,
            readLocks: [0, 0, 0, 0, 0],
            isWriteLocked: false,
            importPending: false,
        },
        drain: {
            mode0Active: false,
            drainPending: false,
            transactionsBlocked: false,
            initiator: null,
            drainStartedAt: null,
            drainTimeoutAt: null,
        },
    }
}

type ExplicitTransactionState =
    | { inTransaction: true; transactionType: 'deferred' | 'immediate' }
    | { inTransaction: false; transactionType: undefined }

function getExplicitTransactionState(sql: string): ExplicitTransactionState | null {
    const normalized = sql.trim().replace(/;+$/, '').replace(/\s+/g, ' ').toUpperCase()
    if (!normalized) {
        return null
    }

    const beginMatch = normalized.match(/^BEGIN(?: (DEFERRED|IMMEDIATE|EXCLUSIVE))?(?: TRANSACTION)?$/)
    if (beginMatch) {
        return {
            inTransaction: true,
            transactionType: beginMatch[1] === 'IMMEDIATE' || beginMatch[1] === 'EXCLUSIVE'
                ? 'immediate'
                : 'deferred',
        }
    }

    if (/^(?:COMMIT|END)(?: TRANSACTION)?$/.test(normalized) || /^ROLLBACK(?: TRANSACTION)?$/.test(normalized)) {
        return {
            inTransaction: false,
            transactionType: undefined,
        }
    }

    return null
}

// Keep this classifier aligned with QueryView so the SDK and UI agree on
// which statements may use the streaming path without skipping write hooks.
const STREAM_READ_ONLY_SQL_TOKENS = new Set(['SELECT', 'VALUES', 'PRAGMA', 'EXPLAIN'])
const STREAM_MUTATING_SQL_TOKENS = new Set(['INSERT', 'UPDATE', 'DELETE', 'REPLACE'])

function isSqlIdentifierStart(char: string | undefined): boolean {
    if (!char) return false
    const code = char.charCodeAt(0)
    return (code >= 65 && code <= 90) || (code >= 97 && code <= 122) || char === '_'
}

function isSqlIdentifierPart(char: string | undefined): boolean {
    if (!char) return false
    const code = char.charCodeAt(0)
    return isSqlIdentifierStart(char) || (code >= 48 && code <= 57) || char === '$'
}

function skipSqlQuotedLiteral(sql: string, startIndex: number): number {
    const quote = sql[startIndex]
    if (quote == null) return startIndex

    let index = startIndex + 1
    while (index < sql.length) {
        if (quote === '[') {
            if (sql[index] === ']') {
                if (sql[index + 1] === ']') {
                    index += 2
                    continue
                }
                return index + 1
            }
            index += 1
            continue
        }

        if (sql[index] === quote) {
            if (sql[index + 1] === quote) {
                index += 2
                continue
            }
            return index + 1
        }
        index += 1
    }
    return index
}

function skipSqlTrivia(sql: string, startIndex: number): number {
    let index = startIndex
    while (index < sql.length) {
        const char = sql[index]
        if (char == null) return index

        if (/\s/.test(char) || char === ';') {
            index += 1
            continue
        }

        if (char === '-' && sql[index + 1] === '-') {
            index += 2
            while (index < sql.length && sql[index] !== '\n') {
                index += 1
            }
            continue
        }

        if (char === '/' && sql[index + 1] === '*') {
            index += 2
            while (index < sql.length && !(sql[index] === '*' && sql[index + 1] === '/')) {
                index += 1
            }
            if (index < sql.length) {
                index += 2
            }
            continue
        }

        break
    }
    return index
}

function readSqlIdentifierToken(sql: string, startIndex: number): { token: string | null; nextIndex: number } {
    const index = skipSqlTrivia(sql, startIndex)
    if (!isSqlIdentifierStart(sql[index])) {
        return { token: null, nextIndex: index }
    }

    let nextIndex = index + 1
    while (nextIndex < sql.length && isSqlIdentifierPart(sql[nextIndex])) {
        nextIndex += 1
    }

    return {
        token: sql.slice(index, nextIndex).toUpperCase(),
        nextIndex,
    }
}

function classifyWithClauseStatement(sql: string, startIndex: number): string | null {
    let index = startIndex
    let depth = 0

    while (index < sql.length) {
        index = skipSqlTrivia(sql, index)
        if (index >= sql.length) {
            return null
        }

        const char = sql[index]
        if (char == null) {
            return null
        }

        if (char === '\'' || char === '"' || char === '`' || char === '[') {
            index = skipSqlQuotedLiteral(sql, index)
            continue
        }

        if (char === '(') {
            depth += 1
            index += 1
            continue
        }

        if (char === ')') {
            depth = Math.max(0, depth - 1)
            index += 1
            continue
        }

        if (depth === 0 && isSqlIdentifierStart(char)) {
            const { token, nextIndex } = readSqlIdentifierToken(sql, index)
            if (token && (STREAM_READ_ONLY_SQL_TOKENS.has(token) || STREAM_MUTATING_SQL_TOKENS.has(token))) {
                return token
            }
            index = nextIndex
            continue
        }

        index += 1
    }

    return null
}

function isStreamReadOnlySql(sql: string): boolean {
    const { token, nextIndex } = readSqlIdentifierToken(sql, 0)
    if (!token) {
        return false
    }

    if (STREAM_READ_ONLY_SQL_TOKENS.has(token)) {
        return true
    }

    if (token !== 'WITH') {
        return false
    }

    return STREAM_READ_ONLY_SQL_TOKENS.has(classifyWithClauseStatement(sql, nextIndex) ?? '')
}

function isWorkerResponse(message: unknown): message is BrowserSqliteWorkerResponse {
    return !!message
        && typeof message === 'object'
        && 'kind' in message
        && (message as { kind?: unknown }).kind === 'response'
        && 'requestId' in message
        && 'ok' in message
}

function assertBrowserSqliteWorkerEnvironment(): void {
    if (typeof SharedArrayBuffer === 'undefined') {
        throw new Error('sqlite3mt in browser requires SharedArrayBuffer; UI-thread fallback is forbidden.')
    }
    if (typeof crossOriginIsolated !== 'undefined' && !crossOriginIsolated) {
        throw new Error('sqlite3mt in browser requires crossOriginIsolated=true; UI-thread fallback is forbidden.')
    }
}

class WorkerRpc<TRequest extends { kind: 'request'; requestId: number }, TEvent extends { kind: string } = never> {
    private nextRequestId = 1
    private readonly pending = new Map<number, PendingRequest>()
    private onEvent: ((event: TEvent) => void) | null = null
    private onFailure: ((error: Error) => void) | null = null
    private terminalError: Error | null = null
    private terminated = false

    constructor(private readonly worker: Worker) {
        worker.onmessage = (event: MessageEvent<BrowserSqliteWorkerResponse | TEvent>) => {
            const msg = event.data
            if (!msg || typeof msg !== 'object' || !("kind" in msg)) {
                return
            }

            if (isWorkerResponse(msg)) {
                sdkConsole.trace('[sqlite3mt:browser-main] rpc:response', {
                    requestId: msg.requestId,
                    ok: msg.ok,
                })
                const pending = this.pending.get(msg.requestId)
                if (!pending) return
                this.pending.delete(msg.requestId)
                if (pending.timeout != null) {
                    clearTimeout(pending.timeout)
                }
                if (msg.ok) {
                    pending.resolve(msg.result)
                } else {
                    pending.reject(deserializeError(msg.error))
                }
                return
            }

            this.onEvent?.(msg as TEvent)
        }

        worker.onerror = (event: ErrorEvent) => {
            this.fail(new Error(event.message || 'sqlite worker failed'), true)
        }
        worker.onmessageerror = () => {
            this.fail(new Error('sqlite worker message deserialization failed'), true)
        }
    }

    setEventHandler(handler: (event: TEvent) => void): void {
        this.onEvent = handler
    }

    setFailureHandler(handler: (error: Error) => void): void {
        this.onFailure = handler
    }

    request<TResult>(
        request: RpcPayload<TRequest>,
        transfer: Transferable[] = [],
    ): Promise<TResult> {
        if (this.terminalError != null) {
            return Promise.reject(this.terminalError)
        }

        const requestId = this.nextRequestId++
        const message = {
            kind: 'request',
            requestId,
            ...request,
        } as unknown as TRequest

        sdkConsole.trace('[sqlite3mt:browser-main] rpc:request', {
            requestId,
            op: 'op' in request ? request.op : undefined,
            connId: 'connId' in request ? request.connId : undefined,
            sql: 'sql' in request ? request.sql : undefined,
        })

        return new Promise<TResult>((resolve, reject) => {
            const op = 'op' in request ? String(request.op) : 'unknown'
            let timeout: ReturnType<typeof setTimeout> | null = null
            if (browserSqliteRpcUsesWatchdog(request)) {
                const timeoutMs = readBrowserSqliteRpcTimeoutMs()
                timeout = setTimeout(() => {
                    const pending = this.pending.get(requestId)
                    if (!pending) {
                        return
                    }
                    const error = new Error(
                        `sqlite3mt browser worker RPC timed out after ${timeoutMs}ms: ${op}#${requestId}`
                    )
                    sdkConsole.warn('[sqlite3mt:browser-main] rpc:timeout', {
                        requestId,
                        op,
                        timeoutMs,
                    })
                    this.pending.delete(requestId)
                    pending.reject(error)
                    if (browserSqliteRpcTimeoutIsTerminal(request)) {
                        this.terminated = true
                        this.worker.terminate()
                        this.fail(error, true)
                    }
                }, timeoutMs)
            }
            this.pending.set(requestId, { resolve, reject, timeout })
            try {
                this.worker.postMessage(message, transfer)
            } catch (error) {
                const pending = this.pending.get(requestId)
                if (pending) {
                    if (pending.timeout != null) {
                        clearTimeout(pending.timeout)
                    }
                    this.pending.delete(requestId)
                }
                reject(error)
            }
        })
    }

    terminate(): void {
        if (this.terminated) {
            return
        }
        this.terminated = true
        this.worker.terminate()
        this.fail(new Error('sqlite worker terminated'), false)
    }

    private fail(reason: unknown, notifyFailure: boolean): void {
        const error = reason instanceof Error ? reason : new Error(String(reason))
        if (this.terminalError == null) {
            this.terminalError = error
        }

        for (const pending of this.pending.values()) {
            if (pending.timeout != null) {
                clearTimeout(pending.timeout)
            }
            pending.reject(this.terminalError)
        }
        this.pending.clear()

        if (notifyFailure) {
            this.onFailure?.(this.terminalError)
        }
    }
}

class BrowserSqliteConnectionProxy implements SqliteConnection {
    private _isOpen = true
    private diag: ConnectionDiag
    private operationTail: Promise<void> = Promise.resolve()
    private recoverTail: Promise<void> | null = null

    constructor(
        private readonly controlRpc: WorkerRpc<BrowserSqliteControlWorkerRequest, BrowserSqliteControlWorkerEvent>,
        private rpc: WorkerRpc<BrowserSqliteClientWorkerRequest>,
        private connId: number,
        private runtimeEpoch: bigint,
        initialDiagnostics: ConnectionDiag,
        private readonly onClosed: (connId: number) => void,
        private readonly onReconnected: (oldConnId: number, newConnId: number, conn: BrowserSqliteConnectionProxy) => void,
        private readonly onDiagnosticsChanged: () => void,
        private readonly reconnectClient: (failedConnId: number, options?: ConnectionOptions) => Promise<{ result: BrowserSqliteConnectResult; clientRpc: WorkerRpc<BrowserSqliteClientWorkerRequest> }>,
    ) {
        this.diag = initialDiagnostics
        this.installClientFailureHandler()
    }

    get isOpen(): boolean { return this._isOpen }

    get pageCount(): number {
        // BrowserSqliteConnectionProxy communicates with a worker; page count
        // is available via the worker's diagnostics but not exposed synchronously.
        return 0
    }

    diagnostics(): ConnectionDiag {
        return this.diag
    }

    async execute<T = Record<string, unknown>>(
        sql: string,
        params?: SqlParam[],
        options?: QueryOptions,
    ): Promise<SqliteStatementResult<T>> {
        return this.enqueueConnectionOperation(async () => {
            options?.signal?.throwIfAborted()
            const t0 = performance.now()
            const managedCheckpoint = createManagedWalCheckpointResult<T>(sql, params)
            if (managedCheckpoint) {
                this.recordStatementMetrics(managedCheckpoint, t0)
                logSqlStatement({
                    connId: this.connId,
                    sql,
                    paramsCount: params?.length ?? 0,
                    durationMs: performance.now() - t0,
                    rowCount: managedCheckpoint.rows.length,
                    changes: managedCheckpoint.changes,
                    ok: true,
                })
                return managedCheckpoint
            }

            try {
                const result = await this.executeOnce<T>(sql, params)
                this.recordStatementMetrics(result, t0)
                this.applyExplicitTransactionState(sql)
                logSqlStatement({
                    connId: this.connId,
                    sql,
                    paramsCount: params?.length ?? 0,
                    durationMs: performance.now() - t0,
                    rowCount: result.rows.length,
                    changes: result.changes,
                    ok: true,
                })
                return result
            } catch (error) {
                if (await this.recoverAfterClientSqliteFailure(error)) {
                    try {
                        const result = await this.executeOnce<T>(sql, params)
                        this.recordStatementMetrics(result, t0)
                        this.applyExplicitTransactionState(sql)
                        logSqlStatement({
                            connId: this.connId,
                            sql,
                            paramsCount: params?.length ?? 0,
                            durationMs: performance.now() - t0,
                            rowCount: result.rows.length,
                            changes: result.changes,
                            ok: true,
                        })
                        return result
                    } catch (retryError) {
                        this.recordStatementFailure(t0)
                        logSqlStatement({
                            connId: this.connId,
                            sql,
                            paramsCount: params?.length ?? 0,
                            durationMs: performance.now() - t0,
                            ok: false,
                            error: retryError,
                        })
                        throw retryError
                    }
                }
                this.recordStatementFailure(t0)
                logSqlStatement({
                    connId: this.connId,
                    sql,
                    paramsCount: params?.length ?? 0,
                    durationMs: performance.now() - t0,
                    ok: false,
                    error,
                })
                throw error
            }
        })
    }

    async query<T = Record<string, unknown>>(sql: string, params?: SqlParam[], options?: QueryOptions): Promise<T[]> {
        return (await this.execute<T>(sql, params, options)).rows
    }

    async exec(sql: string, params?: SqlParam[], options?: QueryOptions): Promise<ExecResult> {
        const result = await this.execute(sql, params, options)
        if (result.changes === 0) {
            this.recordZeroChangeWrite()
        }
        return {
            changes: result.changes,
            lastInsertRowId: result.lastInsertRowId,
        }
    }

    /**
     * Stream rows progressively from a read query using chunked LIMIT/OFFSET
     * execution against the client worker (§149).
     *
     * The worker protocol only supports `conn-execute` (full result), so v1
     * streaming paginates with `LIMIT chunkSize OFFSET n` to avoid loading
     * the entire result set at once. Each internal chunk is 100 rows.
     *
     * - Respects `AbortSignal` for cancellation.
     * - Wraps the original SQL in a subquery pagination envelope.
     * - Yields rows one at a time for progressive consumption.
     */
    async *each<T = Record<string, unknown>>(sql: string, params?: SqlParam[], options?: QueryOptions): AsyncIterable<T> {
        this.assertOpen()
        options?.signal?.throwIfAborted()

        const CHUNK_SIZE = 100
        // Strip trailing semicolons — they are invalid inside the subquery
        // pagination envelope `SELECT * FROM ({sql}) LIMIT … OFFSET …`.
        const trimmed = sql.trim().replace(/;+$/, '')

        // Only paginate statements that are clearly read-only. This keeps
        // mutating CTEs on the eager path while letting read-only `WITH ...
        // SELECT` queries benefit from streaming.
        const isReadOnly = isStreamReadOnlySql(trimmed)

        if (!isReadOnly) {
            // Non-read queries: execute eagerly, yield all rows
            const rows = await this.query<T>(sql, params, options)
            for (const row of rows) {
                yield row
            }
            return
        }

        // Streaming path: paginate with LIMIT/OFFSET
        let offset = 0
        while (true) {
            options?.signal?.throwIfAborted()

            const paginatedSql = `SELECT * FROM (${trimmed}) LIMIT ${CHUNK_SIZE} OFFSET ${offset}`
            const rows = await this.query<T>(paginatedSql, params, options)

            for (const row of rows) {
                yield row
            }

            if (rows.length < CHUNK_SIZE) {
                // Last page — no more rows
                return
            }

            offset += CHUNK_SIZE
        }
    }

    async prepare<T = Record<string, unknown>>(sql: string): Promise<PreparedStatement<T>> {
        this.assertOpen()
        let finalized = false
        const ensureOpen = () => {
            if (finalized) throw new Error('Statement already finalized')
        }

        return {
            all: async (params?: SqlParam[]) => {
                ensureOpen()
                return this.query<T>(sql, params)
            },
            get: async (params?: SqlParam[]) => {
                ensureOpen()
                return (await this.query<T>(sql, params))[0] ?? null
            },
            run: async (params?: SqlParam[]) => {
                ensureOpen()
                return this.exec(sql, params)
            },
            finalize: async () => { finalized = true },
            [Symbol.asyncDispose]: async () => { finalized = true },
        }
    }

    async transaction<T>(fn: (tx: SqliteTransaction) => Promise<T>, options?: TransactionOptions): Promise<T> {
        return this.enqueueConnectionOperation(async () => {
            let txId: number | null = null
            let commitAttempted = false
            try {
                try {
                    txId = await this.beginTransactionOnce(!!options?.immediate)
                } catch (error) {
                    if (!await this.recoverAfterClientSqliteFailure(error)) {
                        throw error
                    }
                    txId = await this.beginTransactionOnce(!!options?.immediate)
                }
                this.setDiag({
                    ...this.diag,
                    inTransaction: true,
                    transactionType: options?.immediate ? 'immediate' : 'deferred',
                })
                const activeTxId = txId
                const recordStatementMetrics = this.recordStatementMetrics.bind(this)
                const recordStatementFailure = this.recordStatementFailure.bind(this)
                const recordZeroChangeWrite = this.recordZeroChangeWrite.bind(this)
                const tx: SqliteTransaction = {
                    execute: async <T2 = Record<string, unknown>>(sql: string, params?: SqlParam[]) => {
                        const t0 = performance.now()
                        try {
                            const result = await this.rpc.request<SqliteStatementResult<T2>>({
                                op: 'tx-execute',
                                txId: activeTxId,
                                sql,
                                params,
                            })
                            recordStatementMetrics(result, t0)
                            logSqlStatement({
                                connId: this.connId,
                                txId: activeTxId,
                                sql,
                                paramsCount: params?.length ?? 0,
                                durationMs: performance.now() - t0,
                                rowCount: result.rows.length,
                                changes: result.changes,
                                ok: true,
                            })
                            return result
                        } catch (error) {
                            recordStatementFailure(t0)
                            logSqlStatement({
                                connId: this.connId,
                                txId: activeTxId,
                                sql,
                                paramsCount: params?.length ?? 0,
                                durationMs: performance.now() - t0,
                                ok: false,
                                error,
                            })
                            throw error
                        }
                    },
                    query: async <T2 = Record<string, unknown>>(sql: string, params?: SqlParam[]) => {
                        return (await tx.execute<T2>(sql, params)).rows
                    },
                    exec: async (sql: string, params?: SqlParam[]) => {
                        const result = await tx.execute(sql, params)
                        if (result.changes === 0) {
                            recordZeroChangeWrite()
                        }
                        return {
                            changes: result.changes,
                            lastInsertRowId: result.lastInsertRowId,
                        }
                    },
                    each: async function* <T2 = Record<string, unknown>>(sql: string, params?: SqlParam[]) {
                        const rows = await tx.query<T2>(sql, params)
                        for (const row of rows) yield row
                    },
                }

                const result = await fn(tx)
                commitAttempted = true
                await this.rpc.request<void>({ op: 'tx-commit', txId: activeTxId })
                txId = null
                return result
            } catch (error) {
                if (txId != null && !commitAttempted) {
                    try {
                        await this.rpc.request<void>({ op: 'tx-rollback', txId })
                    } catch {
                        // rollback best-effort
                    }
                }
                throw error
            } finally {
                this.setDiag({
                    ...this.diag,
                    inTransaction: false,
                    transactionType: undefined,
                })
            }
        })
    }

    async close(): Promise<void> {
        if (!this._isOpen) return
        this._isOpen = false
        try {
            await Promise.race([
                this.rpc.request<void>({ op: 'conn-close', connId: this.connId }),
                new Promise<void>(resolve => {
                    setTimeout(resolve, CONNECTION_CLOSE_GRACE_MS)
                }),
            ])
        } finally {
            try {
                await this.controlRpc.request<void>({ op: 'db-release-client', connId: this.connId })
            } catch {
                // releasing the control-plane slot is best-effort during shutdown
            }
            this.rpc.terminate()
            this.setDiag({ ...this.diag, isOpen: false, inTransaction: false, transactionType: undefined })
            this.onClosed(this.connId)
        }
    }

    async [Symbol.asyncDispose](): Promise<void> {
        await this.close()
    }

    markClosed(): void {
        this._isOpen = false
        this.rpc.terminate()
        this.setDiag({ ...this.diag, isOpen: false, inTransaction: false, transactionType: undefined })
    }

    private assertOpen(): void {
        if (!this._isOpen) throw new Error('Connection is closed')
    }

    private enqueueConnectionOperation<T>(operation: () => Promise<T>): Promise<T> {
        this.assertOpen()
        const previous = this.operationTail
        let release!: () => void
        this.operationTail = new Promise<void>(resolve => {
            release = resolve
        })
        return previous.catch(() => {}).then(async () => {
            try {
                this.assertOpen()
                return await operation()
            } finally {
                release()
            }
        })
    }

    private recordStatementMetrics(
        result: Pick<SqliteStatementResult, 'changes'>,
        startedAt: number,
    ): void {
        this.setDiag({
            ...this.diag,
            totalQueries: this.diag.totalQueries + 1,
            totalWrites: this.diag.totalWrites + (result.changes > 0 ? 1 : 0),
            lastOperationMs: performance.now() - startedAt,
        })
    }

    private async executeOnce<T = Record<string, unknown>>(sql: string, params?: SqlParam[]): Promise<SqliteStatementResult<T>> {
        return await this.rpc.request<SqliteStatementResult<T>>({
            op: 'conn-execute',
            connId: this.connId,
            sql,
            params,
        })
    }

    private async beginTransactionOnce(immediate: boolean): Promise<number> {
        return await this.rpc.request<number>({
            op: 'tx-begin',
            connId: this.connId,
            immediate,
        })
    }

    private async recoverAfterClientSqliteFailure(error: unknown): Promise<boolean> {
        if (!shouldReopenConnectionAfterSqliteFailure(error)) return false
        if (this.diag.inTransaction) return false
        if (this.recoverTail != null) {
            await this.recoverTail
            return true
        }

        const previousConnId = this.connId
        const previousRpc = this.rpc
        const label = this.diag.label
        this.recoverTail = (async () => {
            sdkConsole.warn('[sqlite3mt:browser-main] recovering connection after client sqlite failure', {
                connId: previousConnId,
                label,
            })
            const opened = await this.reconnectClient(previousConnId, { label })
            previousRpc.terminate()
            this.rpc = opened.clientRpc
            this.connId = opened.result.connId
            this.runtimeEpoch = opened.result.attach.runtimeEpoch
            this.installClientFailureHandler()
            this.setDiag({
                ...opened.result.diagnostics,
                totalQueries: this.diag.totalQueries,
                totalWrites: this.diag.totalWrites,
                lastOperationMs: this.diag.lastOperationMs,
                inTransaction: false,
                transactionType: undefined,
            })
            this.onReconnected(previousConnId, this.connId, this)
        })()

        try {
            await this.recoverTail
            return true
        } catch (recoverError) {
            sdkConsole.error('[sqlite3mt:browser-main] connection recovery failed', recoverError)
            return false
        } finally {
            this.recoverTail = null
        }
    }

    private recordZeroChangeWrite(): void {
        this.setDiag({
            ...this.diag,
            totalWrites: this.diag.totalWrites + 1,
        })
    }

    private recordStatementFailure(startedAt: number): void {
        this.setDiag({
            ...this.diag,
            lastOperationMs: performance.now() - startedAt,
        })
    }

    private applyExplicitTransactionState(sql: string): void {
        const nextState = getExplicitTransactionState(sql)
        if (!nextState) {
            return
        }

        this.setDiag({
            ...this.diag,
            inTransaction: nextState.inTransaction,
            transactionType: nextState.transactionType,
        })
    }

    private setDiag(diag: ConnectionDiag): void {
        this.diag = diag
        this.onDiagnosticsChanged()
    }

    private handleWorkerFailure(): void {
        if (!this._isOpen) {
            return
        }

        this._isOpen = false
        this.rpc.terminate()
        this.setDiag({ ...this.diag, isOpen: false, inTransaction: false, transactionType: undefined })
        this.onClosed(this.connId)

        void this.controlRpc.request<void>({ op: 'db-release-client', connId: this.connId }).catch(() => {
            // releasing the control-plane slot is best-effort after worker failure
        })
    }

    private installClientFailureHandler(): void {
        this.rpc.setFailureHandler(() => {
            this.handleWorkerFailure()
        })
    }
}

class BrowserSqliteDatabaseProxy implements SqliteDatabase {
    private readonly syncListeners = new Set<(event: SyncEvent) => void>()
    private readonly diagnosticsListeners = new Set<(diag: DatabaseDiagnostics) => void>()
    private readonly connections = new Map<number, BrowserSqliteConnectionProxy>()
    private connectionInitQueue: Promise<void> | null = null
    private _isOpen = true
    private durableScopeReleased = false
    private _syncMode: SyncMode = 'concurrent' as SyncMode
    private diag: DatabaseDiagnostics
    /**
     * Optional listener for sqlite3mt debug-stage events (§145).
     * When set, debug-stage events are forwarded here instead of just console.info.
     * The webapp uses this to route events into the RuntimeEventTrace ring buffer.
     */
    private debugStageListener: ((payload: unknown) => void) | null = null
    /** §145 runtime trace context — set during initialize() so debug-stage events can be dispatched to window.dispatchEvent. */
    private traceContext: { blobId: string; path: string; workspaceName: string } | null = null

    constructor(
        private readonly rpc: WorkerRpc<BrowserSqliteControlWorkerRequest, BrowserSqliteControlWorkerEvent>,
        workspaceName: string,
        private readonly releaseDurableScopeLease: (() => Promise<void>) | null,
    ) {
        this.diag = emptyDiagnostics(workspaceName)
        this.rpc.setEventHandler(event => {
            if (event.kind === 'sync-event') {
                if (event.event.type === 'mode-change') {
                    this._syncMode = event.event.to
                }
                for (const listener of this.syncListeners) {
                    try { listener(event.event) } catch { /* listener errors are isolated */ }
                }
                return
            }

            if (event.kind === 'debug-stage') {
                if (this.traceContext) {
                    dispatchBrowserRuntimeTraceEvent({
                        kind: 'sqlite:debug-stage',
                        blobId: this.traceContext.blobId,
                        path: this.traceContext.path,
                        workspaceName: this.traceContext.workspaceName,
                        payload: event.payload,
                    })
                }
                if (this.debugStageListener) {
                    try { this.debugStageListener(event.payload) } catch { /* listener errors are isolated */ }
                } else {
                    sdkConsole.trace('[sqlite3mt:browser-main] debug-stage', event.payload)
                }
                return
            }

            this.diag = this.withLocalConnectionDiagnostics(event.diag)
            this._syncMode = event.diag.sync.mode
            for (const listener of this.diagnosticsListeners) {
                try { listener(this.diag) } catch { /* listener errors are isolated */ }
            }
        })
        this.rpc.setFailureHandler(() => {
            if (!this._isOpen) {
                return
            }

            this._isOpen = false
            for (const conn of this.connections.values()) {
                conn.markClosed()
            }
            this.connections.clear()
            this.syncLocalConnectionDiagnostics()
            void this.releaseDurableScope()
        })
    }

    async initialize(config: BrowserSqliteOpenConfig): Promise<void> {
        const result = await this.rpc.request<BrowserSqliteInitResult>({ op: 'init', config })
        this.diag = this.withLocalConnectionDiagnostics(result.diagnostics)
        this._syncMode = result.syncMode
        this.traceContext = {
            blobId: config.blobId,
            path: config.path,
            workspaceName: buildBrowserSqliteWorkspaceName(config.blobId, config.path),
        }
    }

    get syncMode(): SyncMode { return this._syncMode }
    get isOpen(): boolean { return this._isOpen }
    get connectionCount(): number { return Array.from(this.connections.values()).filter(conn => conn.isOpen).length }

    /**
     * Set a listener for sqlite3mt debug-stage events (§145).
     * When set, events are forwarded to the listener instead of console.info.
     * Pass null to clear the listener and restore console.info logging.
     */
    setDebugStageListener(listener: ((payload: unknown) => void) | null): void {
        this.debugStageListener = listener
    }

    async connect(options?: ConnectionOptions): Promise<SqliteConnection> {
        return await this.enqueueConnectionInit(async () => {
            this.assertOpen()
            let opened = await this.connectClientWorker(options)
            if (!opened.ok) {
                const firstError = opened.error
                try {
                    const recovered = await this.rpc.request<BrowserSqliteInitResult>({
                        op: 'db-recover-client-init-failure',
                        failedConnId: opened.connId,
                    })
                    this.diag = this.withLocalConnectionDiagnostics(recovered.diagnostics)
                    this._syncMode = recovered.syncMode
                    opened = await this.connectClientWorker(options)
                } catch {
                    throw firstError
                }
                if (!opened.ok) {
                    throw opened.error
                }
            }

            const conn = new BrowserSqliteConnectionProxy(
                this.rpc,
                opened.clientRpc,
                opened.result.connId,
                opened.result.attach.runtimeEpoch,
                opened.result.diagnostics,
                connId => {
                    this.connections.delete(connId)
                    this.syncLocalConnectionDiagnostics()
                },
                (oldConnId, newConnId, conn) => {
                    this.connections.delete(oldConnId)
                    this.connections.set(newConnId, conn)
                    this.syncLocalConnectionDiagnostics()
                },
                () => this.syncLocalConnectionDiagnostics(),
                (failedConnId, reconnectOptions) => this.reconnectClientWorkerAfterIoFailure(failedConnId, reconnectOptions),
            )
            this.connections.set(opened.result.connId, conn)
            this.syncLocalConnectionDiagnostics()
            return conn
        })
    }

    private async connectClientWorker(options?: ConnectionOptions): Promise<
        | { ok: true; result: BrowserSqliteConnectResult; clientRpc: WorkerRpc<BrowserSqliteClientWorkerRequest> }
        | { ok: false; connId: number; error: unknown }
    > {
        const channel = new MessageChannel()
        const result = await this.rpc.request<BrowserSqliteConnectResult>(
            { op: 'db-connect', options, port: channel.port1 },
            [channel.port1],
        )

        const worker = new Worker(
            new URL('./browserSqlite.client.worker.ts', import.meta.url),
            { type: 'module' },
        )
        const clientRpc = new WorkerRpc<BrowserSqliteClientWorkerRequest>(worker)

        try {
            await clientRpc.request<void>(
                { op: 'init-connection', attach: result.attach, port: channel.port2 },
                [channel.port2],
            )
            return { ok: true, result, clientRpc }
        } catch (error) {
            clientRpc.terminate()
            channel.port2.close()
            try {
                await this.rpc.request<void>({ op: 'db-release-client', connId: result.connId })
            } catch {
                // release best-effort if the client worker failed during init
            }
            return { ok: false, connId: result.connId, error }
        }
    }

    private async reconnectClientWorkerAfterIoFailure(
        failedConnId: number,
        options?: ConnectionOptions,
    ): Promise<{ result: BrowserSqliteConnectResult; clientRpc: WorkerRpc<BrowserSqliteClientWorkerRequest> }> {
        const recovered = await this.rpc.request<BrowserSqliteInitResult>({
            op: 'db-recover-client-io-failure',
            failedConnId,
        })
        this.diag = this.withLocalConnectionDiagnostics(recovered.diagnostics)
        this._syncMode = recovered.syncMode
        const opened = await this.connectClientWorker(options)
        if (!opened.ok) {
            throw opened.error
        }
        return opened
    }

    private async flushInternal(remote: boolean, options?: SqliteFlushLocalOptions): Promise<void> {
        await this.rpc.request<void>({
            op: 'db-flush',
            remote,
            ...(options?.wait === false ? { wait: false } : {}),
        })
    }

    async flushLocal(options?: SqliteFlushLocalOptions): Promise<void> {
        this.assertOpen()
        await this.flushInternal(false, options)
    }

    async flushRemote(): Promise<void> {
        this.assertOpen()
        await this.flushInternal(true)
    }

    async close(options?: SqliteCloseOptions): Promise<void> {
        if (!this._isOpen) return
        this._isOpen = false

        await this.connectionInitQueue?.catch(() => undefined)

        const openConnections = Array.from(this.connections.values())
        for (const conn of openConnections) {
            try {
                await conn.close()
            } catch {
                conn.markClosed()
            }
        }

        try {
            await this.rpc.request<void>({
                op: 'db-close',
                flushRemote: options?.flushRemote === true,
                flushLocal: options?.flushLocal !== false,
            })
        } finally {
            for (const conn of this.connections.values()) {
                conn.markClosed()
            }
            this.connections.clear()
            this.syncLocalConnectionDiagnostics()
            this.rpc.terminate()
            await this.releaseDurableScope()
        }

    }

    async [Symbol.asyncDispose](): Promise<void> {
        await this.close()
    }

    async setSyncMode(mode: SyncMode): Promise<void> {
        this.assertOpen()
        await this.rpc.request<void>({ op: 'db-set-sync-mode', mode })
        this._syncMode = mode
    }

    async startSync(options?: { interval?: number }): Promise<void> {
        this.assertOpen()
        await this.rpc.request<void>({ op: 'db-start-sync', interval: options?.interval })
    }

    async stopSync(): Promise<void> {
        this.assertOpen()
        await this.rpc.request<void>({ op: 'db-stop-sync' })
    }

    async setWorkspaceSyncPolicy(policy: WorkspaceSyncPolicy): Promise<void> {
        this.assertOpen()
        await this.rpc.request<void>({ op: 'db-set-workspace-sync-policy', policy })
    }

    async syncNow(): Promise<SyncResult> {
        this.assertOpen()
        return await this.rpc.request<SyncResult>({ op: 'db-sync-now' })
    }

    private async enqueueConnectionInit<T>(action: () => Promise<T>): Promise<T> {
        const previous = this.connectionInitQueue
        let releaseQueue!: () => void
        const queueEntry = new Promise<void>(resolve => {
            releaseQueue = resolve
        })
        this.connectionInitQueue = queueEntry

        if (previous != null) {
            await previous.catch(() => undefined)
        }

        try {
            return await action()
        } finally {
            if (this.connectionInitQueue === queueEntry) {
                this.connectionInitQueue = null
            }
            releaseQueue()
        }
    }

    onSync(callback: (event: SyncEvent) => void): () => void {
        this.syncListeners.add(callback)
        return () => {
            this.syncListeners.delete(callback)
        }
    }

    diagnostics(): DatabaseDiagnostics {
        return this.diag
    }

    __debugGetLiveDiagnostics = async (): Promise<DatabaseDiagnostics> => {
        return this.withLocalConnectionDiagnostics(
            await this.rpc.request<DatabaseDiagnostics>({ op: 'db-diagnostics' }),
        )
    }

    onDiagnostics(callback: (diag: DatabaseDiagnostics) => void): () => void {
        this.diagnosticsListeners.add(callback)
        return () => {
            this.diagnosticsListeners.delete(callback)
        }
    }

    private assertOpen(): void {
        if (!this._isOpen) throw new Error('Database is closed')
    }

    private async releaseDurableScope(): Promise<void> {
        if (this.durableScopeReleased) {
            return
        }

        this.durableScopeReleased = true
        await this.releaseDurableScopeLease?.()
    }

    private withLocalConnectionDiagnostics(diag: DatabaseDiagnostics): DatabaseDiagnostics {
        return {
            ...diag,
            connections: this.snapshotConnectionDiagnostics(),
        }
    }

    private snapshotConnectionDiagnostics(): ConnectionDiag[] {
        return Array.from(this.connections.values())
            .filter(conn => conn.isOpen)
            .map(conn => conn.diagnostics())
    }

    private syncLocalConnectionDiagnostics(): void {
        this.diag = {
            ...this.diag,
            timestamp: performance.now(),
            connections: this.snapshotConnectionDiagnostics(),
        }
    }
}

export function shouldUseBrowserSqliteWorkerOnUiThread(): boolean {
    return typeof document !== 'undefined'
        && typeof window !== 'undefined'
        && typeof Worker !== 'undefined'
}

export async function openBrowserSqliteDatabase(config: BrowserSqliteOpenConfig): Promise<SqliteDatabase> {
    assertBrowserSqliteWorkerEnvironment()
    const durableScopeLease = await acquireBrowserDurableScopeLease(config)
    const workerConfig = durableScopeLease == null
        ? config
        : {
            ...config,
            sqlite3MtDurableScope: durableScopeLease.scope,
        }

    sdkConsole.debug('[sqlite3mt:browser-main] openBrowserSqliteDatabase', {
        blobId: config.blobId,
        path: config.path,
        workspace: buildBrowserSqliteWorkspaceName(config.blobId, config.path),
        backgroundSync: config.backgroundSync ?? 'workspace-policy',
        durableScope: durableScopeLease?.scope ?? null,
        durableScopeIsSecondary: durableScopeLease?.isSecondaryScope ?? false,
        crossOriginIsolated: typeof crossOriginIsolated === 'undefined' ? null : crossOriginIsolated,
    })

    const worker = new Worker(
        new URL('./browserSqlite.worker.ts', import.meta.url),
        { type: 'module' }
    )
    const rpc = new WorkerRpc<BrowserSqliteControlWorkerRequest, BrowserSqliteControlWorkerEvent>(worker)
    const proxy = new BrowserSqliteDatabaseProxy(
        rpc,
        buildBrowserSqliteWorkspaceName(config.blobId, config.path),
        durableScopeLease?.release ?? null,
    )

    try {
        await proxy.initialize(workerConfig)
        return proxy
    } catch (error) {
        rpc.terminate()
        await durableScopeLease?.release()
        throw error
    }
}
